import React, { useState } from 'react';
import { X, Package, Hash, Tag, Truck, Calendar, FileText, History, CircleDollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HistoryDialog from '../HistoryDialog/HistoryDialog';
import { formatConsommableRule, getDisplayType } from '../../utils/consommableQuantity';
import './ViewConsommableDialog.css';

const ViewConsommableDialog = ({ isOpen, onClose, consommable }) => {
  const { t, i18n } = useTranslation();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  if (!isOpen || !consommable) return null;
  const displayType = getDisplayType(consommable.type_consommable);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="dialog-overlay" onClick={handleOverlayClick}>
      <div className="dialog-container view-dialog">
        <div className="dialog-header">
          <div className="dialog-title">
            <Package size={24} />
            <h2>{t('consommables.viewDialog.title')}</h2>
          </div>
          <button className="dialog-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="dialog-content">
          <div className="detail-section">
            <h3 className="consommable-name">{consommable.name}</h3>
            <span className={`type-badge type-${displayType.toLowerCase()}`}>
              {t(`consommables.types.${displayType}`)}
            </span>
          </div>

          <div className="details-grid">
            <div className="detail-item">
              <div className="detail-label">
                {t('consommables.viewDialog.unitPrice')}
              </div>
              <div className="detail-value price">
                {parseFloat(consommable.unit_price).toFixed(2)} {consommable.devise || consommable.supplier_details?.devise || 'EUR'}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <CircleDollarSign size={16} />
                {t('consommables.viewDialog.devise')}
              </div>
              <div className="detail-value">
                {consommable.devise || t('consommables.devise.inheritFromSupplier')}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Hash size={16} />
                {t('consommables.viewDialog.quantity')}
              </div>
              <div className="detail-value">
                {formatConsommableRule(consommable, t)}
                {consommable.is_per_day ? ` (${t('consommables.perDay')})` : ''}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Tag size={16} />
                {t('consommables.viewDialog.formations')}
              </div>
              <div className="detail-value">
                {consommable.formation_title || '-'}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-label">
                <Truck size={16} />
                {t('consommables.viewDialog.supplier')}
              </div>
              <div className="detail-value">
                {consommable.supplier_details?.name || '-'}
              </div>
            </div>

            {consommable.supplier_details?.contact_email && (
              <div className="detail-item full-width">
                <div className="detail-label">
                  <FileText size={16} />
                  {t('consommables.viewDialog.supplierContact')}
                </div>
                <div className="detail-value">
                  {consommable.supplier_details.contact_email}
                </div>
              </div>
            )}

            <div className="detail-item">
              <div className="detail-label">
                <Calendar size={16} />
                {t('consommables.viewDialog.createdAt')}
              </div>
              <div className="detail-value">
                {new Date(consommable.created_at).toLocaleDateString(i18n.language)}
              </div>
            </div>
          </div>
        </div>

        <div className="dialog-actions single">
          <button className="btn-secondary" type="button" onClick={() => setIsHistoryOpen(true)}>
            <History size={18} /> {t('common.history')}
          </button>
          <button className="btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>

      <HistoryDialog
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        title={t('history.title')}
        appLabel="catalog"
        model="consommable"
        objectId={consommable?.id}
      />
    </div>
  );
};

export default ViewConsommableDialog;
